import { useState, useEffect } from "react";
import { Button } from "rsuite";
import FilterComponent from "../../components/FilterComponent";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import reload from "../../assets/reload.svg";
import moment from "moment";
import {
  useGetGraphDataMutation,
  useGetFilterDataMutation,
} from "../../services/redux/api/cannibalization";
import Graph from "../../common/Graph";
import { IRoot } from "../../services/interface/IHome";
import { useSelector } from "react-redux";

interface IGraphPayload {
  dc: string[];
  storeNumber: string[];
  subCategoryOnAd: string;
  acrossSubCategory: string;
}

const SUB_CATEGORY_LABEL_MAP: Record<string, string> = {
  LAMBLOIN: "Category 1",
  GROUNDTURKEY: "Category 2",
  BULKDELIMEAT: "Category 3",
  CHICKENTENDERS: "Category 4",
  BEEFGRINDS: "Category 5",
  CHICKENWINGS: "Category 6",
  CHICKENDRUMSTICKS: "Category 7",
  CHICKENTHIGHS: "Category 8",
  CHICKENBREASTS: "Category 9",
  "BEEFPLATE-BRISKET": "Category 10",
};

const getSubCategoryLabel = (value?: string) => {
  if (!value) return "";
  return SUB_CATEGORY_LABEL_MAP[value] || value;
};

function Cannibalization() {
  const [isVisible, setIsVisible] = useState(true);
  const [dynamicFilters, setDynamicFilters] = useState<{
    [key: string]: any[];
  }>({});
  const lastUpdated =
    useSelector((state: IRoot) => state.filter.lastUpdateDate) || "";
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(
    null
  );

  const [selectedFilterData, setSelectedFilterData] = useState<{
    [key: string]: string[];
  }>({
    dc: [],
    storeNumber: [],
  });
  const [graphDataTop, setGraphDataTop] = useState<any[]>([]);
  const [graphDataBottom, setGraphDataBottom] = useState<any[]>([]);
  const [filters, setFilters] = useState<IGraphPayload>({
    dc: [],
    storeNumber: [],
    subCategoryOnAd: "",
    acrossSubCategory: "",
  });
  const [getFilterData, { isLoading: isFilterLoading }] =
    useGetFilterDataMutation();

  const [
    getGraphData,
    {
      isLoading: isGraphLoading,
      data: graphDataCannibization,
      error: graphError,
    },
  ] = useGetGraphDataMutation();

  const fetchFilterData = async () => {
    try {
      const payload: IGraphPayload = {
        dc: selectedFilterData["dc"] || [],
        storeNumber: selectedFilterData["storeNumber"] || [],
        subCategoryOnAd: "",
        acrossSubCategory: "",
      };

      const response = await getFilterData(payload).unwrap();
      console.log("Filter Data:", response?.data);

      // Transform the API response into the expected structure
      const transformedFilters = {
        dc:
          response?.data?.dc?.map((item: string) => ({
            displayValue: item, // For UI label
            filterValue: item, // For back-end filter value
          })) || [],
        storeNumber:
          response?.data?.storeNumber?.map((item: string) => ({
            displayValue: item,
            filterValue: item,
          })) || [],
      };

      setDynamicFilters(transformedFilters);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  useEffect(() => {
    fetchGraphData("", "");
  }, [filters]);

  const handleFilterChange = (filterKey: string, values: string[]) => {

    // Update the selected filter data dynamically
    setSelectedFilterData((prevState) => ({
      ...prevState,
      [filterKey]: values,
    }));
  };
  const handleApplyFilters = () => {
    setSelectedSubCategory(null); 
    // Sync selected filters to the main `filters` state
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...selectedFilterData, // Apply selected filters (dc, storeNumber, etc.)
    }));
  };

  const handleClearFilters = () => {
    setSelectedSubCategory(null); 
    // Clear selected filters
    setSelectedFilterData({
      dc: [],
      storeNumber: [],
    });

    // Reset main filters state
    setFilters({
      dc: [],
      storeNumber: [],
      subCategoryOnAd: "",
      acrossSubCategory: "",
    });
  };
  const filterShowHandler = () => {
    setIsVisible((prev) => !prev);
  };

  const fetchGraphData = async (graphTop: string, graphBottom: string) => {
    try {
      const payload: IGraphPayload = {
        dc: filters.dc,
        storeNumber: filters.storeNumber,
        subCategoryOnAd: graphTop ? graphTop : filters.subCategoryOnAd,
        acrossSubCategory: graphBottom ? graphBottom : filters.acrossSubCategory,
      };


      const response = await getGraphData(payload).unwrap();

      setGraphDataTop(response?.data?.subCategory || []);
      setGraphDataBottom(response?.data?.acrossSubCategory || []);
    } catch (error: any) {
      console.error("Error fetching graph data:", error);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, [selectedFilterData]);

  const handleGraphData = ({ subCategoryOnAd, SubCategory,}: { subCategoryOnAd?: string; SubCategory?: string; }) => {
    const selectedCategory = subCategoryOnAd || SubCategory;
    if (selectedCategory) {
      setSelectedSubCategory(selectedCategory); 
      fetchGraphData(subCategoryOnAd || "", SubCategory || ""); 
    }
  };

  return (
    <>
      <div className="relative">
        <div
          className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${
            !isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"
          }`}
        >
          <div className="flex justify-between items-center mb-1">
            <h3 className="page-title font-semibold">{"Promotions & Assessment > Cannibalization"}</h3>
            {
              <div className="flex gap-2">
                <img src={reload} alt="reload" className="w-[1vw]" />
                <p className="text-fs16 text-[#858585] font-semibold">
                  Last Updated :{" "}
                  <span>
                    {moment(lastUpdated?.slice(0, 10)).format("M/DD/YYYY")}
                  </span>
                </p>
              </div>
            }
          </div>

          {/* Filters Section */}
          {isVisible && (
            <div className="grid grid-cols-[1fr_auto] items-end gap-4">
              <div className="grid grid-cols-12 gap-2 items-end">
                {/* Render Filters */}
                {dynamicFilters &&
                  Object.entries(dynamicFilters).map(([filterKey, options]) => (
                    <FilterComponent
                      key={filterKey}
                      loading={isFilterLoading}
                      filterKey={filterKey}
                      filterName={
                        filterKey === "dc" 
                          ? "DC" 
                          : filterKey === "storeNumber" 
                          ? "Retail Store" 
                          : filterKey.charAt(0).toUpperCase() + filterKey.slice(1)
                      }
                      options={options.map((opt) => ({
                        label: opt.displayValue,
                        value: opt.filterValue,
                      }))}
                      labelKey="label"
                      valueKey="value"
                      value={selectedFilterData[filterKey] || []} // Dynamically bind the selected state
                      onFilterChange={handleFilterChange}
                      onDropdownOpen={() => {}}
                    />
                  ))}
              </div>
              <div className="align-right flex gap-1">
                <Button
                  onClick={handleClearFilters}
                  className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Clear
                </Button>

                <Button
                  onClick={handleApplyFilters}
                  className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer z-[10]" onClick={filterShowHandler}>
            {isVisible && (
              <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />
            )}
            {!isVisible && (
              <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />
            )}
          </div>
        </div>
      </div>
      <div className="bg-[#F1F1F1] flex px-2 py-1"></div>

      {isGraphLoading && (
        <div>
          <CustomLoader open={isGraphLoading}/>
        </div>
      )}
      {!isGraphLoading && ( 
        <>
      <div className={`bg-[#F1F1F1] flex px-[2%] pb-2 h-[35vh]`}>
        <Graph
          title="Avg. Ad Lift By Sub-Category (Last 3M)"
          graphTypes={["bar"]}
          data={graphDataTop.map((d) => ({
            ...d,
            displaySubCategoryOnAd: getSubCategoryLabel(d.SubCategoryOnAd),
            fill:
              selectedSubCategory && d.SubCategoryOnAd !== selectedSubCategory
                ? "rgba(0, 128, 247, 0.3)" // Transparent for non-selected bars
                : "#0080F7", // Opaque for selected bar
          }))}
          dataKey={["AvgAdChange"]}
          dataName={["Sub-Category"]}
          fill={["#0080F7"]}
          width={50}
          xAxisKey={"displaySubCategoryOnAd"}
          onClick={(event) => handleGraphData({ subCategoryOnAd: event?.SubCategoryOnAd })}
          isRequiredYAxisFormatter={true}
        />
      </div>

      <div className={`bg-[#F1F1F1] flex px-[2%] pb-2 h-[35vh]`}>
        <Graph
          title="Cannibalization Across Sub-Category (Last 3M)"
          graphTypes={["bar"]}
          data={graphDataBottom.map((d) => ({
            ...d,
            displaySubCategory: getSubCategoryLabel(d.SubCategory),
            fill:
              selectedSubCategory && d.SubCategory !== selectedSubCategory
                ? "rgba(115, 183, 97, 0.3)" // Transparent for non-selected bars
                : "#73B761", // Opaque for selected bar
          }))}
          dataKey={["adAverage"]}
          dataName={["Across Sub-Category"]}
          fill={["#73B761"]}
          width={50}
          xAxisKey={"displaySubCategory"}
          onClick={(event) => handleGraphData({ SubCategory: event?.SubCategory })}
          isRequiredYAxisFormatter={true}
        />
      </div>
      </>)}
    </>
  );
}

export default Cannibalization;
